/**
 * Intranet Osasco Plaza — src/modules/acesso.jsx
 * Telas do módulo Controle de Acesso (Docas) — entrada e saída de veículos
 * nas docas de entrega. Portado do protótipo antigo (submenu de Segurança)
 * pro padrão D1 + Worker do projeto. Ver DOC/ADR/0017.
 *
 * Mesmas listas fechadas que o `acesso-worker.js` aceita — duplicadas aqui
 * de propósito (mesmo padrão de PERIODICIDADES/AVISOS_DIAS em seg.jsx):
 * divergir aqui só produz uma mensagem de erro visível do worker, nunca
 * corrompe dado, porque quem manda a última palavra é sempre o servidor.
 */
import { Blocked, Icon, hasPerm, maskPhone, validarCPF } from './shared.jsx';
import { baixarQRPng, imprimirQR, qrGenerateSVG } from './qr.js';
const { useState, useEffect, useMemo, useRef } = React;

const TIPOS_VEICULO = [
  { id: 'carro',    label: 'Carro',    icon: 'truck' },
  { id: 'moto',     label: 'Moto',     icon: 'truck' },
  { id: 'van',      label: 'Van',      icon: 'truck' },
  { id: 'caminhao', label: 'Caminhão', icon: 'truck' },
];

const FINALIDADES = [
  'Entrega em loja', 'Delivery', 'Retirada de delivery', 'Serviço / Manutenção',
  'Coleta', 'Mudança / Carga e descarga', 'Outro',
];

const DOCAS = Array.from({ length: 8 }, (_, i) => `Doca ${i + 1}`);

/* Só formatação visual (maiúsculas) — a validação de verdade (formato
   antigo ABC1234 ou Mercosul ABC1D23) é feita no worker; aqui só existe pra
   dar feedback rápido antes de enviar. */
function fmtPlaca(v) {
  return String(v || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 7);
}
const RE_PLACA_ANTIGA   = /^[A-Z]{3}[0-9]{4}$/;
const RE_PLACA_MERCOSUL = /^[A-Z]{3}[0-9][A-Z][0-9]{2}$/;
function placaParecevalida(p) {
  return RE_PLACA_ANTIGA.test(p) || RE_PLACA_MERCOSUL.test(p);
}

/* ── Leitura de placa por voz (Fase Acesso-5) ──
   O reconhecimento de fala (Web Speech API) às vezes devolve dígitos por
   extenso em vez do algarismo — "sete" em vez de "7" — principalmente
   quando o segurança fala pausadamente, letra por letra. `fmtPlaca` sozinho
   não resolveria isso (ele só filtra A-Z0-9), então esta função normaliza
   palavra a palavra ANTES de aplicar `fmtPlaca`. Mesma filosofia do OCR:
   ajuda visual, nunca validação — quem decide se a placa está certa é o
   segurança olhando o campo (editável) antes de registrar a entrada. */
const NUM_POR_EXTENSO = {
  zero:'0', um:'1', uma:'1', dois:'2', duas:'2', tres:'3', 'três':'3',
  quatro:'4', cinco:'5', seis:'6', sete:'7', oito:'8', nove:'9',
};
function normalizarFalaPlaca(transcript) {
  const palavras = String(transcript || '').toLowerCase().trim().split(/\s+/);
  const chars = palavras.map(p => {
    const limpo = p.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]/gi, '');
    return NUM_POR_EXTENSO[limpo] || NUM_POR_EXTENSO[p] || limpo;
  }).join('');
  return fmtPlaca(chars);
}

/* ── Documento do motorista (CPF ou CNH) ──
   Diferente do CPF "puro" de Solicitações/Recrutamento (que usam
   `maskCpf`/`validarCPF` do shared.jsx sem ressalva), este campo aceita
   CPF OU CNH — ambos com 11 dígitos numéricos no Brasil, mas CNH não
   segue o dígito verificador de CPF nem se escreve com pontos/hífen.
   Forçar o mascaramento visual (000.000.000-00) e reprovar como "CPF
   inválido" quebraria a digitação de uma CNH legítima. Por isso aqui só
   filtra dígitos (sem pontuação) e a validação de CPF vira reforço
   POSITIVO opcional — nunca erro — quando bate com o formato de CPF. */
function maskDocumento(v) {
  return String(v || '').replace(/\D/g, '').slice(0, 11);
}
function documentoPareceCpfValido(v) {
  const d = String(v || '').replace(/\D/g, '');
  return d.length === 11 && validarCPF(d);
}

/* ── Redimensiona a foto da placa antes de mandar pro OCR (Fase Acesso-3) ──
   Mesma ideia do `fpVisionRedimensionar` do Folha de Pagamento do VicStaffO:
   uma placa não precisa de resolução alta pra ser lida, e mandar a foto
   original do celular (4-12 MB) estouraria o limite de payload à toa. Máx.
   1280px no maior lado, JPEG 0.85 — sobra bastante nitidez pros 7
   caracteres de uma placa. */
function redimensionarParaOCR(file, maxDim = 1280, qualidade = 0.85) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width: w, height: h } = img;
      if (w > maxDim || h > maxDim) {
        const escala = maxDim / Math.max(w, h);
        w = Math.round(w * escala); h = Math.round(h * escala);
      }
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      canvas.getContext('2d').drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL('image/jpeg', qualidade));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Não foi possível abrir a imagem.')); };
    img.src = url;
  });
}

/* "1h23min" a partir de um timestamp ISO de entrada. */
function permanencia(entradaEm) {
  const min = Math.max(0, Math.floor((Date.now() - new Date(entradaEm).getTime()) / 60000));
  const h = Math.floor(min / 60), m = min % 60;
  return h > 0 ? `${h}h${String(m).padStart(2, '0')}min` : `${m}min`;
}

function blankForm() {
  return {
    placa: '', tipoVeiculo: 'carro', motoristaNome: '', motoristaDoc: '', telefone: '',
    empresa: '', lojaId: '', finalidade: FINALIDADES[0], doca: '', obs: '',
  };
}

/* ── Carregamento preguiçoso do jsQR — mesma lib e mesmo domínio já
   liberados no script-src da CSP desde a Fase Ronda-1 (DOC/ADR/0013). Cópia
   enxuta do helper de ronda.jsx — não dá pra importar (não é exportado de
   lá), e duplicar aqui é mais simples que criar um módulo compartilhado só
   pra uma função de 10 linhas. */
let _jsQRPromise = null;
function carregarJsQR() {
  if (window.jsQR) return Promise.resolve(window.jsQR);
  if (_jsQRPromise) return _jsQRPromise;
  _jsQRPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://cdn.jsdelivr.net/npm/jsqr@1.4.0/dist/jsQR.js';
    s.onload = () => resolve(window.jsQR);
    s.onerror = () => reject(new Error('Não foi possível carregar o leitor de QR.'));
    document.head.appendChild(s);
  });
  return _jsQRPromise;
}

/* ── Leitor de QR do Motorista por câmera ao vivo (getUserMedia + jsQR) ──
   Mesma estrutura do `LeitorQR` de ronda.jsx: video oculto + canvas de
   trabalho + requestAnimationFrame lendo frame a frame até achar um código. */
function LeitorQRMotorista({ onLido, onCancelar }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const [erro, setErro] = useState('');
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const jsQR = await carregarJsQR();
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
        if (!vivo) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        if (!vivo) return;
        setCarregando(false);
        const tick = () => {
          if (!vivo) return;
          const video = videoRef.current, canvas = canvasRef.current;
          if (video && canvas && video.readyState === video.HAVE_ENOUGH_DATA) {
            canvas.width = video.videoWidth; canvas.height = video.videoHeight;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const code = jsQR(imgData.data, imgData.width, imgData.height);
            if (code && code.data) { onLido(code.data); return; }
          }
          rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
      } catch (e) {
        if (vivo) setErro(e.message || 'Não foi possível acessar a câmera.');
      }
    })();
    return () => {
      vivo = false;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    };
  }, []);

  return (
    <div className="modal-overlay" onClick={onCancelar}>
      <div className="modal-card" onClick={e => e.stopPropagation()} style={{ maxWidth: 400, textAlign: 'center' }}>
        <button className="close-x" onClick={onCancelar}><Icon name="x" size={16}/></button>
        <h3>Escanear QR do Motorista</h3>
        {erro ? (
          <p style={{ color: 'var(--err)', fontSize: 13, marginTop: 12 }}>{erro}</p>
        ) : (
          <>
            {carregando && <p style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 8 }}>Abrindo câmera…</p>}
            <video ref={videoRef} playsInline muted style={{ width: '100%', borderRadius: 10, marginTop: 12, background: '#000' }}/>
            <canvas ref={canvasRef} style={{ display: 'none' }}/>
            <p style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 10 }}>Aponte para o QR que o motorista mostrar no celular.</p>
          </>
        )}
      </div>
    </div>
  );
}

/* ── Registro (Registrar Entrada + Veículos no Interior) ── */
export function AcessoRegistroScreen({ user }) {
  const [tab, setTab]           = useState('registrar'); // 'registrar' | 'ativos'
  const [ativos, setAtivos]     = useState([]);
  const [lojas, setLojas]       = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erroCarga, setErroCarga]   = useState('');
  const [form, setForm]         = useState(blankForm());
  const [foto, setFoto]         = useState(null);
  const [fotoPreview, setFotoPreview] = useState(null);
  const [busy, setBusy]         = useState(false);
  const [err, setErr]           = useState('');
  const [sucesso, setSucesso]   = useState(null); // registro recém-criado
  const [scanAberto, setScanAberto] = useState(false);
  const [buscaCpf, setBuscaCpf]     = useState('');
  const [buscandoCpf, setBuscandoCpf] = useState(false);
  const [autopreenchido, setAutopreenchido] = useState(null); // nome do motorista, pra mostrar o aviso
  const [ocrBusy, setOcrBusy]     = useState(false);
  const [ocrErr, setOcrErr]       = useState('');
  const [ocrSugestao, setOcrSugestao] = useState(null); // { placa, confianca } — nunca aplica sozinho
  const [micBusy, setMicBusy]     = useState(false);
  const [micErr, setMicErr]       = useState('');
  const [buscandoPlaca, setBuscandoPlaca] = useState(false);
  const [placaBuscaMsg, setPlacaBuscaMsg] = useState('');

  const canEdit = hasPerm(user, 'acesso', 'edit'); // quem libera saída

  const refresh = async () => {
    setCarregando(true); setErroCarga('');
    try {
      const [a, l] = await Promise.all([API.acesso.ativos(), API.listarLojas()]);
      setAtivos(a.registros || []); setLojas((l.lojas || []).filter(x => x.ativo !== 0));
    } catch (e) {
      setErroCarga(e.message || 'Não foi possível carregar os dados.');
    }
    setCarregando(false);
  };
  useEffect(() => { refresh(); }, []);

  /* Cronômetro de permanência — só precisa re-renderizar a cada minuto, não
     precisa de estado próprio: o cálculo é derivado de `entradaEm` toda vez
     que o componente renderiza, e este tick só força esse re-render. */
  const [, forceTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => forceTick(n => n + 1), 30000);
    return () => clearInterval(t);
  }, []);

  const handleFoto = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (fotoPreview) URL.revokeObjectURL(fotoPreview);
    setFoto(file);
    setFotoPreview(URL.createObjectURL(file));
  };
  const limparFoto = () => {
    if (fotoPreview) URL.revokeObjectURL(fotoPreview);
    setFoto(null); setFotoPreview(null);
  };

  /* Preenche só identidade do motorista/veículo — placa, loja, finalidade e
     doca continuam em branco de propósito (podem mudar visita a visita,
     mesmo pro mesmo entregador; o porteiro sempre confirma). */
  const aplicarMotorista = (m) => {
    setForm(f => ({
      ...f,
      motoristaNome: m.nome, motoristaDoc: m.documento, telefone: m.telefone || f.telefone,
      empresa: m.empresa || f.empresa, tipoVeiculo: m.tipoVeiculo || f.tipoVeiculo,
      placa: m.placaHabitual ? fmtPlaca(m.placaHabitual) : f.placa,
    }));
    setAutopreenchido(m.nome);
  };

  const onQrLido = async (codigo) => {
    setScanAberto(false);
    try {
      const r = await API.acesso.motoristas.buscarPorQr(codigo);
      aplicarMotorista(r.motorista);
    } catch (e) {
      setErr('QR lido, mas não achei o cadastro: ' + (e.message || 'erro desconhecido'));
    }
  };

  const buscarPorCpf = async () => {
    if (!buscaCpf.trim()) return;
    setBuscandoCpf(true); setErr('');
    try {
      const r = await API.acesso.motoristas.buscarPorDocumento(buscaCpf.trim());
      aplicarMotorista(r.motorista);
      setBuscaCpf('');
    } catch (e) {
      setErr(e.message || 'Motorista não encontrado para este CPF/CNH.');
    }
    setBuscandoCpf(false);
  };

  /* Fase Acesso-5.2 — mesma ideia do buscarPorCpf, mas pela placa: pedido
     do dono depois de testar o microfone (a placa cai certa no campo, mas
     nada disparava a consulta do motorista frequente). Chamada tanto pelo
     botão manual quanto automaticamente pelo mic/OCR assim que o valor
     aplicado já bate no formato de placa — não dispara a cada tecla digitada
     (só resultado de leitura automática ou clique explícito), pra não sair
     testando placa incompleta a cada caractere. 404 aqui não é erro de
     verdade — é o caso normal de "não é motorista frequente", então mostra
     como aviso neutro, não em vermelho. */
  const buscarMotoristaPorPlaca = async (placaConsulta) => {
    const alvo = fmtPlaca(placaConsulta);
    if (!placaParecevalida(alvo)) return;
    setBuscandoPlaca(true); setPlacaBuscaMsg('');
    try {
      const r = await API.acesso.motoristas.buscarPorPlaca(alvo);
      aplicarMotorista(r.motorista);
      setPlacaBuscaMsg('');
    } catch (e) {
      setPlacaBuscaMsg(e.message || 'Nenhum motorista frequente cadastrado com essa placa.');
    }
    setBuscandoPlaca(false);
  };

  /* OCR de placa (Fase Acesso-3) — foto entra, sugestão sai. Nunca escreve
     em `form.placa` sozinho: fica em `ocrSugestao` até o porteiro confirmar
     (`aplicarSugestaoPlaca`), pelo mesmo motivo do QR/CPF não travarem o
     resto do formulário — confusão O/0, I/1 é comum o bastante numa leitura
     por foto pra não confiar cego numa placa de acesso.
     Fase Acesso-4 (2026-09-12, DOC/PLANO_ACESSO4_ABONO_ESTACIONAMENTO.md):
     mesmo botão, mesma rota — o porteiro pode apontar a câmera pro ticket de
     estacionamento (campo "Placa (LPR)" impresso) em vez do veículo; o
     prompt do worker já sabe distinguir os dois casos. */
  const lerPlacaComIA = async (e) => {
    const file = e.target.files[0];
    e.target.value = ''; // permite selecionar o mesmo arquivo de novo depois
    if (!file) return;
    setOcrBusy(true); setOcrErr(''); setOcrSugestao(null);
    try {
      const dataUrl = await redimensionarParaOCR(file);
      const r = await API.acesso.ocrPlaca(dataUrl);
      if (r.placa) setOcrSugestao({ placa: r.placa, confianca: r.confianca });
      else setOcrErr(r.aviso || 'Não foi possível ler a placa. Digite manualmente.');
    } catch (e2) {
      setOcrErr(e2.message || 'Não foi possível ler a placa. Digite manualmente.');
    }
    setOcrBusy(false);
  };
  const aplicarSugestaoPlaca = () => {
    if (!ocrSugestao) return;
    const placa = fmtPlaca(ocrSugestao.placa);
    setForm(f => ({ ...f, placa }));
    setOcrSugestao(null);
    buscarMotoristaPorPlaca(placa);
  };

  /* Fase Acesso-5 — mic ao lado do botão IA: o segurança fala a placa e ela
     já aparece no campo (editável, igual digitar). Diferente do OCR, aqui
     não existe um passo de "sugestão + confirmar": a fala já é a própria
     ação do segurança lendo a placa, então escreve direto em `form.placa`
     — a conferência de sempre acontece no próprio campo, antes de enviar. */
  const speechSupported = typeof window !== 'undefined'
    && !!(window.SpeechRecognition || window.webkitSpeechRecognition);
  const lerPlacaPorVoz = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setMicErr('Reconhecimento de voz não é suportado neste navegador.'); return; }
    setMicErr('');
    const rec = new SR();
    rec.lang = 'pt-BR';
    rec.interimResults = false;
    /* Fase Acesso-5.1 — 1 alternativa só não bastava: testado ao vivo, "UFA7H74"
       falado saiu "US7H74"/"UFS7H74" na 1ª opção (o reconhecimento confunde o
       F/A soletrado com um som de S) e correta em alguma das seguintes. Pede
       várias hipóteses e escolhe a que já bate no formato de placa em vez de
       confiar cegamente na primeira — mesmo cuidado do OCR, adaptado pro
       problema específico da voz (é a lista INTEIRA que vem incerta, não só
       uma leitura). Nunca é validação de verdade (isso é sempre no campo,
       editável, e no worker) — só reduz quanto o segurança precisa corrigir. */
    rec.maxAlternatives = 8;
    rec.continuous = false;
    rec.onstart = () => setMicBusy(true);
    /* Fase Acesso-5.3 — `continuous:false` devia bastar pra parar sozinho
       depois do resultado final, mas na prática (testado ao vivo) o
       reconhecimento do navegador continua "ouvindo" — ícone de gravação
       aceso — até o timeout de silêncio dele mesmo, que demora vários
       segundos; sem gente falando de novo, ele nunca chega nesse silêncio
       porque o teste em si é rápido. `rec.stop()` explícito aqui força o
       fim assim que já temos o resultado, em vez de esperar o navegador
       decidir sozinho — dispara `onend` na hora, solta o mic e libera o
       botão de novo. */
    rec.onresult = (ev) => {
      const alternativas = ev.results && ev.results[0] ? Array.from(ev.results[0]) : [];
      const candidatos = alternativas.map(a => normalizarFalaPlaca(a.transcript)).filter(Boolean);
      const placa = candidatos.find(placaParecevalida) || candidatos[0] || '';
      if (placa) { setForm(f => ({ ...f, placa })); buscarMotoristaPorPlaca(placa); }
      else setMicErr('Não entendi a placa. Tente novamente ou digite.');
      try { rec.stop(); } catch {}
    };
    rec.onerror = (ev) => {
      if (ev.error === 'not-allowed' || ev.error === 'service-not-allowed') setMicErr('Permissão de microfone negada.');
      else if (ev.error === 'no-speech') setMicErr('Não ouvi nada. Tente de novo.');
      else setMicErr('Não foi possível ouvir a placa. Tente novamente.');
      try { rec.stop(); } catch {}
    };
    rec.onend = () => setMicBusy(false);
    try { rec.start(); } catch { setMicBusy(false); }
  };

  const registrarEntrada = async () => {
    const placa = fmtPlaca(form.placa);
    if (!placaParecevalida(placa)) { setErr('Placa inválida. Use o formato antigo (ABC1234) ou Mercosul (ABC1D23).'); return; }
    if (!form.motoristaNome.trim()) { setErr('Informe o nome do motorista.'); return; }
    if (!form.lojaId) { setErr('Selecione a loja de destino.'); return; }
    if (!form.doca) { setErr('Selecione a doca.'); return; }

    setBusy(true); setErr('');
    try {
      const r = await API.acesso.registrarEntrada({ ...form, placa });
      if (foto) {
        /* O registro já está gravado mesmo se o envio da foto falhar — é
           preferível ter o registro sem foto do que perder o registro
           inteiro por causa de rede instável no upload da imagem. */
        try { await API.acesso.anexarFoto(r.registro.id, foto); }
        catch (e) { setErr('Registro salvo, mas a foto não foi enviada: ' + (e.message || 'erro desconhecido') + '.'); }
      }
      setSucesso(r.registro);
      setForm(blankForm()); limparFoto(); setAutopreenchido(null); setOcrSugestao(null); setOcrErr('');
      await refresh();
    } catch (e) {
      setErr(e.message || 'Não foi possível registrar a entrada.');
    }
    setBusy(false);
  };

  const registrarSaida = async (id) => {
    try { await API.acesso.registrarSaida(id); await refresh(); }
    catch (e) { alert(e.message || 'Não foi possível registrar a saída.'); }
  };

  if (!hasPerm(user, 'acesso', 'view')) return <Blocked/>;

  const hoje = ativos.filter(r => new Date(r.entradaEm).toDateString() === new Date().toDateString());
  const motos = ativos.filter(r => r.tipoVeiculo === 'moto');
  const cargas = ativos.filter(r => r.tipoVeiculo === 'van' || r.tipoVeiculo === 'caminhao');

  return (
    <div className="main-pad">
      <div className="page-title-row">
        <div className="page-title">
          <h2>🚚 Controle de Acesso — Docas</h2>
          <p>Entrada e saída de veículos nas docas de entrega</p>
        </div>
      </div>

      {erroCarga && (
        <div className="card" style={{ padding:16, marginBottom:16, background:'var(--err-bg)', border:'1px solid #f5c2c2' }}>
          <div style={{ color:'var(--err)', fontSize:13, fontWeight:600, marginBottom:6 }}>{erroCarga}</div>
          <button className="btn btn-ghost" onClick={refresh}>Tentar de novo</button>
        </div>
      )}

      <div className="seg-stat-row">
        <div className="seg-stat-box"><div className="sv" style={{color:'var(--ink)'}}>{hoje.length}</div><div className="sl">Acessos hoje</div></div>
        <div className="seg-stat-box"><div className="sv" style={{color:'var(--ok)'}}>{ativos.length}</div><div className="sl">Veículos no interior</div></div>
        <div className="seg-stat-box"><div className="sv" style={{color:'var(--info)'}}>{motos.length}</div><div className="sl">Motos ativas</div></div>
        <div className="seg-stat-box"><div className="sv" style={{color:'var(--warn)'}}>{cargas.length}</div><div className="sl">Van/caminhão ativos</div></div>
      </div>

      <div className="seg-tabs">
        {[['registrar', '📋 Registrar Entrada'], ['ativos', `🔄 Veículos no Interior (${ativos.length})`]].map(([k, l]) => (
          <button key={k} className={`seg-tab ${tab === k ? 'active' : ''}`} onClick={() => setTab(k)}>{l}</button>
        ))}
      </div>

      {tab === 'registrar' && (
        <div className="card">
          {sucesso && (
            <div style={{ padding:12, marginBottom:16, background:'var(--ok-bg)', border:'1px solid var(--ok)', borderRadius:8, fontSize:13, color:'var(--ok)' }}>
              ✓ Entrada registrada — código <strong>{sucesso.codigo}</strong>, {sucesso.doca}.
            </div>
          )}

          {/* Identificação rápida — evita redigitar dado de quem entrega
              todo dia. Preenche só nome/documento/telefone/empresa/tipo de
              veículo; placa/loja/finalidade/doca continuam manuais. */}
          <div className="field">
            <label>Identificação Rápida (opcional)</label>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
              <button className="btn btn-ghost" onClick={() => setScanAberto(true)}><Icon name="qr-code" size={14}/> Escanear QR do Motorista</button>
              <span style={{ fontSize:12, color:'var(--ink-4)' }}>ou</span>
              <input style={{ flex:'1 1 160px', maxWidth:220 }} placeholder="Buscar por CPF/CNH" value={buscaCpf}
                onChange={e => setBuscaCpf(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') buscarPorCpf(); }}/>
              <button className="btn btn-ghost btn-sm" onClick={buscarPorCpf} disabled={buscandoCpf}>{buscandoCpf ? 'Buscando...' : 'Buscar'}</button>
            </div>
            {autopreenchido && (
              <div className="hint" style={{ color:'var(--ok)', marginTop:6 }}>
                ✓ Dados de <strong>{autopreenchido}</strong> preenchidos automaticamente — confira antes de registrar.
              </div>
            )}
          </div>

          <div className="field">
            <label>Tipo de Veículo</label>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
              {TIPOS_VEICULO.map(t => (
                <button key={t.id} onClick={() => setForm(f => ({ ...f, tipoVeiculo: t.id }))} style={{
                  flex:'1 1 100px', height:60, border:'1.5px solid', borderRadius:8, cursor:'pointer', fontFamily:'inherit',
                  display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:4, fontSize:12, fontWeight:600,
                  borderColor: form.tipoVeiculo === t.id ? 'var(--navy)' : 'var(--line)',
                  background: form.tipoVeiculo === t.id ? 'var(--info-bg)' : 'var(--surface)',
                  color: form.tipoVeiculo === t.id ? 'var(--navy)' : 'var(--ink-3)',
                }}>
                  <Icon name={t.icon} size={18}/>{t.label}
                </button>
              ))}
            </div>
          </div>

          <div className="seg-field-row">
            <div className="field">
              <label>Placa <span className="req">*</span></label>
              <div style={{ display:'flex', gap:6 }}>
                <input style={{ flex:1 }} value={form.placa} onChange={e => setForm(f => ({ ...f, placa: fmtPlaca(e.target.value) }))} placeholder="ABC1234"/>
                <input type="file" accept="image/png,image/jpeg" capture="environment" style={{ display:'none' }} id="acesso-ocr-input" onChange={lerPlacaComIA}/>
                <button type="button" className="btn btn-ghost btn-sm" title="Ler placa com IA — foto do veículo ou do ticket de estacionamento"
                  onClick={() => document.getElementById('acesso-ocr-input').click()} disabled={ocrBusy}>
                  {ocrBusy ? '...' : <><Icon name="camera" size={13}/> IA</>}
                </button>
                {speechSupported && (
                  <button type="button" className="btn btn-ghost btn-sm" title="Falar a placa"
                    onClick={lerPlacaPorVoz} disabled={micBusy}>
                    {micBusy ? '🎙️...' : <Icon name="mic" size={13}/>}
                  </button>
                )}
              </div>
              <div className="hint" style={{ color:'var(--ink-4)' }}>
                O botão IA lê a placa tanto de uma foto do veículo quanto do campo "Placa (LPR)" do ticket de estacionamento{speechSupported ? ', e o microfone 🎤 preenche o campo com a placa falada em voz alta — fale devagar, com uma pequena pausa entre cada letra/número (ex.: "U... F... A... sete... H... sete... quatro"), e confira o resultado antes de registrar.' : '.'}
              </div>
              {form.placa && <div className="hint" style={{ color: placaParecevalida(form.placa) ? 'var(--ok)' : 'var(--err)' }}>
                {placaParecevalida(form.placa) ? 'Formato válido' : 'Formato antigo (ABC1234) ou Mercosul (ABC1D23)'}
              </div>}
              {placaParecevalida(form.placa) && (
                <div className="hint" style={{ marginTop:4 }}>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => buscarMotoristaPorPlaca(form.placa)} disabled={buscandoPlaca}>
                    {buscandoPlaca ? 'Buscando...' : <><Icon name="search" size={13}/> Buscar motorista por esta placa</>}
                  </button>
                </div>
              )}
              {placaBuscaMsg && <div className="hint" style={{ color:'var(--ink-4)', marginTop:4 }}>{placaBuscaMsg}</div>}
              {ocrSugestao && (
                <div className="hint" style={{ color:'var(--info)', display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', marginTop:4 }}>
                  🤖 IA leu: <strong>{ocrSugestao.placa}</strong> (confiança {ocrSugestao.confianca})
                  <button type="button" className="btn btn-ghost btn-sm" onClick={aplicarSugestaoPlaca}>Usar esta placa</button>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => setOcrSugestao(null)}>Descartar</button>
                </div>
              )}
              {ocrErr && <div className="hint" style={{ color:'var(--err)', marginTop:4 }}>{ocrErr}</div>}
              {micErr && <div className="hint" style={{ color:'var(--err)', marginTop:4 }}>{micErr}</div>}
            </div>
            <div className="field">
              <label>Doca <span className="req">*</span></label>
              <select value={form.doca} onChange={e => setForm(f => ({ ...f, doca: e.target.value }))}>
                <option value="">Selecione...</option>
                {DOCAS.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
          </div>

          <div className="seg-field-row">
            <div className="field"><label>Nome do Motorista <span className="req">*</span></label>
              <input value={form.motoristaNome} onChange={e => setForm(f => ({ ...f, motoristaNome: e.target.value }))} placeholder="Nome completo"/>
            </div>
            <div className="field"><label>CPF / CNH</label>
              <input value={form.motoristaDoc} onChange={e => setForm(f => ({ ...f, motoristaDoc: maskDocumento(e.target.value) }))} inputMode="numeric" placeholder="Só números"/>
              {documentoPareceCpfValido(form.motoristaDoc) && <div className="hint" style={{ color:'var(--ok)' }}>✓ CPF válido</div>}
            </div>
          </div>

          <div className="seg-field-row">
            <div className="field"><label>Telefone</label>
              <input value={form.telefone} onChange={e => setForm(f => ({ ...f, telefone: maskPhone(e.target.value) }))} placeholder="(XX) XXXXX-XXXX" maxLength={15}/>
            </div>
            <div className="field"><label>Empresa / Transportadora</label>
              <input value={form.empresa} onChange={e => setForm(f => ({ ...f, empresa: e.target.value }))}/>
            </div>
          </div>

          <div className="seg-field-row">
            <div className="field"><label>Loja de Destino <span className="req">*</span></label>
              <select value={form.lojaId} onChange={e => setForm(f => ({ ...f, lojaId: e.target.value }))}>
                <option value="">Selecione a loja...</option>
                {lojas.map(l => <option key={l.id} value={l.id}>{l.nome}</option>)}
              </select>
            </div>
            <div className="field"><label>Finalidade</label>
              <select value={form.finalidade} onChange={e => setForm(f => ({ ...f, finalidade: e.target.value }))}>
                {FINALIDADES.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
          </div>

          <div className="field"><label>Observações</label>
            <textarea rows={2} value={form.obs} onChange={e => setForm(f => ({ ...f, obs: e.target.value }))} placeholder="Número de NF, volumes, etc."/>
          </div>

          <div className="field">
            <label>Foto do Veículo / Placa (opcional)</label>
            <input type="file" accept="image/png,image/jpeg" capture="environment" style={{ display:'none' }} id="acesso-foto-input" onChange={handleFoto}/>
            {fotoPreview ? (
              <div>
                <img src={fotoPreview} alt="Preview" className="seg-photo-preview"/>
                <button className="btn btn-ghost btn-sm" style={{ marginTop:8 }} onClick={limparFoto}><Icon name="x" size={13}/> Remover foto</button>
              </div>
            ) : (
              <button className="btn btn-ghost" onClick={() => document.getElementById('acesso-foto-input').click()}>
                <Icon name="camera" size={14}/> Adicionar foto
              </button>
            )}
          </div>

          {err && <p style={{ color:'var(--err)', fontSize:12, marginBottom:12 }}>{err}</p>}
          <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
            <button className="btn btn-ghost" onClick={() => { setForm(blankForm()); limparFoto(); setErr(''); setSucesso(null); setAutopreenchido(null); setOcrSugestao(null); setOcrErr(''); }}>Limpar</button>
            <button className="btn btn-primary" onClick={registrarEntrada} disabled={busy}>{busy ? 'Registrando...' : 'Registrar Entrada'}</button>
          </div>
        </div>
      )}

      {tab === 'ativos' && (
        carregando ? (
          <div className="card" style={{ padding:20, color:'var(--ink-3)', fontSize:13 }}>Carregando…</div>
        ) : ativos.length === 0 ? (
          <div className="card" style={{ textAlign:'center', padding:40, color:'var(--ink-3)' }}>
            <Icon name="truck" size={32} style={{ margin:'0 auto 12px', display:'block' }}/>
            <p>Nenhum veículo no interior no momento.</p>
          </div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
            {ativos.map(r => {
              const tipo = TIPOS_VEICULO.find(t => t.id === r.tipoVeiculo);
              return (
                <div key={r.id} className="card seg-insp-row">
                  <div className="seg-insp-header">
                    <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                      <Icon name={tipo?.icon || 'truck'} size={20}/>
                      <div>
                        <div className="seg-insp-who">{r.placa} <span style={{ fontSize:11, fontWeight:400, color:'var(--ink-3)' }}>— {tipo?.label}</span></div>
                        <div className="seg-insp-when">{r.motoristaNome} · {r.doca}</div>
                      </div>
                    </div>
                    <span className="status-pill info"><span className="ind"/>No interior · {permanencia(r.entradaEm)}</span>
                  </div>
                  <div style={{ fontSize:12, color:'var(--ink-3)', marginTop:8, display:'flex', gap:14, flexWrap:'wrap' }}>
                    {r.lojaNome && <span>🏬 {r.lojaNome}</span>}
                    <span>🎯 {r.finalidade}</span>
                    {r.empresa && <span>🏢 {r.empresa}</span>}
                    {r.telefone && <span>📞 {r.telefone}</span>}
                    <span>🕐 {new Date(r.entradaEm).toLocaleTimeString('pt-BR')}</span>
                    <span style={{ fontFamily:"'JetBrains Mono',monospace" }}>#{r.codigo}</span>
                  </div>
                  {r.obs && <div className="seg-insp-obs">💬 {r.obs}</div>}
                  {canEdit && (
                    <div style={{ marginTop:10 }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => registrarSaida(r.id)}><Icon name="log-out" size={13}/> Registrar Saída</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      )}

      {scanAberto && <LeitorQRMotorista onLido={onQrLido} onCancelar={() => setScanAberto(false)}/>}
    </div>
  );
}

/* ── Histórico de Acessos ── */
export function AcessoHistoricoScreen({ user }) {
  const [registros, setRegistros] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erroCarga, setErroCarga]   = useState('');
  const [filtros, setFiltros]     = useState({ tipo: '', status: '', doca: '', busca: '' });
  const [sel, setSel]             = useState(null);

  const refresh = async () => {
    setCarregando(true); setErroCarga('');
    try {
      const r = await API.acesso.historico(filtros);
      setRegistros(r.registros || []);
    } catch (e) {
      setErroCarga(e.message || 'Não foi possível carregar o histórico.');
    }
    setCarregando(false);
  };
  useEffect(() => { refresh(); }, [filtros.tipo, filtros.status, filtros.doca]);
  useEffect(() => { const t = setTimeout(refresh, 350); return () => clearTimeout(t); }, [filtros.busca]);

  if (!hasPerm(user, 'acesso', 'view')) return <Blocked/>;

  const hojeStr = new Date().toDateString();
  const semanaAtras = Date.now() - 7 * 86400000;
  const stats = useMemo(() => ({
    total:  registros.length,
    hoje:   registros.filter(r => new Date(r.entradaEm).toDateString() === hojeStr).length,
    semana: registros.filter(r => new Date(r.entradaEm).getTime() >= semanaAtras).length,
    ativos: registros.filter(r => r.status === 'entrada').length,
  }), [registros]);

  return (
    <div className="main-pad">
      <div className="page-title-row">
        <div className="page-title"><h2>📁 Histórico de Acessos</h2><p>Consulta e auditoria de entradas/saídas nas docas</p></div>
      </div>

      {erroCarga && (
        <div className="card" style={{ padding:16, marginBottom:16, background:'var(--err-bg)', border:'1px solid #f5c2c2' }}>
          <div style={{ color:'var(--err)', fontSize:13, fontWeight:600, marginBottom:6 }}>{erroCarga}</div>
          <button className="btn btn-ghost" onClick={refresh}>Tentar de novo</button>
        </div>
      )}

      <div className="seg-stat-row">
        <div className="seg-stat-box"><div className="sv">{stats.total}</div><div className="sl">Total (filtro atual)</div></div>
        <div className="seg-stat-box"><div className="sv" style={{color:'var(--info)'}}>{stats.hoje}</div><div className="sl">Hoje</div></div>
        <div className="seg-stat-box"><div className="sv" style={{color:'var(--navy)'}}>{stats.semana}</div><div className="sl">Últimos 7 dias</div></div>
        <div className="seg-stat-box"><div className="sv" style={{color:'var(--ok)'}}>{stats.ativos}</div><div className="sl">Veículos ativos</div></div>
      </div>

      <div className="card" style={{ marginBottom:16 }}>
        <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
          <input style={{ flex:'2 1 200px' }} placeholder="Buscar por placa, motorista, empresa ou loja..." value={filtros.busca} onChange={e => setFiltros(f => ({ ...f, busca: e.target.value }))}/>
          <select style={{ flex:'1 1 120px' }} value={filtros.tipo} onChange={e => setFiltros(f => ({ ...f, tipo: e.target.value }))}>
            <option value="">Todos os tipos</option>
            {TIPOS_VEICULO.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
          <select style={{ flex:'1 1 120px' }} value={filtros.status} onChange={e => setFiltros(f => ({ ...f, status: e.target.value }))}>
            <option value="">Todos os status</option>
            <option value="entrada">No interior</option>
            <option value="saida">Já saiu</option>
          </select>
          <select style={{ flex:'1 1 120px' }} value={filtros.doca} onChange={e => setFiltros(f => ({ ...f, doca: e.target.value }))}>
            <option value="">Todas as docas</option>
            {DOCAS.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
      </div>

      <div className="card">
        {carregando ? (
          <div style={{ textAlign:'center', padding:32, color:'var(--ink-3)' }}>Carregando…</div>
        ) : registros.length === 0 ? (
          <div style={{ textAlign:'center', padding:32, color:'var(--ink-3)' }}>
            <Icon name="file-text" size={28} style={{ margin:'0 auto 8px', display:'block' }}/>
            <p>Nenhum registro encontrado.</p>
          </div>
        ) : registros.map(r => (
          <div key={r.id} className="seg-insp-row" style={{ cursor:'pointer' }} onClick={() => setSel(r)}>
            <div className="seg-insp-header">
              <div>
                <div className="seg-insp-who">{r.placa} <span style={{ fontSize:11, color:'var(--ink-3)', fontWeight:400 }}>— {r.motoristaNome}</span></div>
                <div className="seg-insp-when">{new Date(r.entradaEm).toLocaleString('pt-BR')} · {r.doca} · {r.lojaNome || '—'}</div>
              </div>
              <span className={`status-pill ${r.status === 'entrada' ? 'info' : 'muted'}`}><span className="ind"/>{r.status === 'entrada' ? 'No interior' : 'Saiu'}</span>
            </div>
          </div>
        ))}
      </div>

      {sel && (
        <div className="modal-overlay" onClick={() => setSel(null)}>
          <div className="modal-card" onClick={e => e.stopPropagation()} style={{ maxWidth:520 }}>
            <button className="close-x" onClick={() => setSel(null)}><Icon name="x" size={16}/></button>
            <h3>#{sel.codigo} — {sel.placa}</h3>
            <p style={{ fontSize:13, color:'var(--ink-3)', marginBottom:20 }}>{TIPOS_VEICULO.find(t => t.id === sel.tipoVeiculo)?.label} · {sel.doca}</p>
            <div className="seg-cols2">
              {[
                ['Motorista', sel.motoristaNome], ['CPF/CNH', sel.motoristaDoc || '—'],
                ['Telefone', sel.telefone || '—'], ['Empresa', sel.empresa || '—'],
                ['Loja', sel.lojaNome || '—'], ['Finalidade', sel.finalidade],
                ['Entrada', `${new Date(sel.entradaEm).toLocaleString('pt-BR')} — ${sel.entradaPorNome}`],
                ['Saída', sel.saidaEm ? `${new Date(sel.saidaEm).toLocaleString('pt-BR')} — ${sel.saidaPorNome}` : 'Ainda no interior'],
              ].map(([k, v]) => (
                <div key={k}><div style={{ fontSize:11, color:'var(--ink-3)', marginBottom:4, fontWeight:600 }}>{k}</div><div style={{ fontSize:14 }}>{v}</div></div>
              ))}
            </div>
            {sel.obs && <div style={{ marginTop:12, fontSize:13 }}><strong>Obs:</strong> {sel.obs}</div>}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── QR do Motorista (Fase Acesso-2) ── O conteúdo do QR é só o `codigoQr`
   (ex: "MOT00001"), não uma URL — quem lê é o leitor de câmera embutido na
   tela de Registrar Entrada, o QR nunca sai do fluxo do app (mesmo
   raciocínio do QR de ponto em ronda.jsx). */
export function AcessoMotoristaQRModal({ motorista, onClose }) {
  const svg = qrGenerateSVG(motorista.codigoQr, { size: 220, margin: 3, level: 'M' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const baixar = async () => {
    setBusy(true); setErr('');
    try { await baixarQRPng(svg, `motorista-${motorista.codigoQr}.png`, 512); }
    catch (e) { setErr(e.message || 'Não foi possível gerar o PNG.'); }
    setBusy(false);
  };
  const imprimir = () => imprimirQR(svg, `${motorista.codigoQr} — ${motorista.nome}`);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" onClick={e => e.stopPropagation()} style={{ maxWidth: 340, textAlign: 'center' }}>
        <button className="close-x" onClick={onClose}><Icon name="x" size={16}/></button>
        <h3>QR do Motorista</h3>
        <p style={{ fontSize: 13, color: 'var(--ink-3)', marginBottom: 16 }}>{motorista.codigoQr} — {motorista.nome}</p>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }} dangerouslySetInnerHTML={{ __html: svg }}/>
        <p style={{ fontSize: 11, color: 'var(--ink-4)', marginBottom: 16 }}>
          Entregue este QR ao motorista (impresso ou foto no celular dele). Na doca, o porteiro aponta a câmera pra ele e o formulário de entrada já vem preenchido.
        </p>
        {err && <p style={{ color: 'var(--err)', fontSize: 12, marginBottom: 12 }}>{err}</p>}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          <button className="btn btn-ghost" onClick={imprimir}><Icon name="printer" size={14}/> Imprimir</button>
          <button className="btn btn-primary" onClick={baixar} disabled={busy}><Icon name="download" size={14}/> {busy ? 'Gerando...' : 'Baixar PNG'}</button>
        </div>
      </div>
    </div>
  );
}

function blankFormMotorista() {
  return { nome: '', documento: '', telefone: '', empresa: '', tipoVeiculo: '', placaHabitual: '' };
}

/* ── Cadastro de Motoristas Frequentes (Fase Acesso-2) ── */
export function AcessoMotoristasScreen({ user }) {
  const [motoristas, setMotoristas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erroCarga, setErroCarga] = useState('');
  const [busca, setBusca] = useState('');
  const [modal, setModal] = useState(null); // null | 'new' | motorista
  const [qrDe, setQrDe] = useState(null);
  const [form, setForm] = useState(blankFormMotorista());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const canEdit = hasPerm(user, 'acesso', 'edit');
  const canDelete = hasPerm(user, 'acesso', 'delete');

  const refresh = async () => {
    setCarregando(true); setErroCarga('');
    try {
      const r = await API.acesso.motoristas.listar();
      setMotoristas(r.motoristas || []);
    } catch (e) {
      setErroCarga(e.message || 'Não foi possível carregar os motoristas.');
    }
    setCarregando(false);
  };
  useEffect(() => { refresh(); }, []);

  if (!hasPerm(user, 'acesso', 'view')) return <Blocked/>;

  const openNew = () => { setForm(blankFormMotorista()); setErr(''); setModal('new'); };
  const openEdit = (m) => {
    setForm({ nome: m.nome, documento: m.documento, telefone: m.telefone, empresa: m.empresa, tipoVeiculo: m.tipoVeiculo || '', placaHabitual: m.placaHabitual });
    setErr(''); setModal(m);
  };

  const save = async () => {
    if (!form.nome.trim()) { setErr('Informe o nome do motorista.'); return; }
    if (!form.documento.trim()) { setErr('Informe o CPF/CNH.'); return; }
    setBusy(true); setErr('');
    try {
      const payload = { ...form, tipoVeiculo: form.tipoVeiculo || null };
      if (modal === 'new') await API.acesso.motoristas.criar(payload);
      else                 await API.acesso.motoristas.editar(modal.id, payload);
      await refresh(); setModal(null);
    } catch (e) {
      setErr(e.message || 'Não foi possível salvar o motorista.');
    }
    setBusy(false);
  };

  const alternarAtivo = async (m) => {
    try { await API.acesso.motoristas.editar(m.id, { ativo: !m.ativo }); await refresh(); }
    catch (e) { alert(e.message || 'Não foi possível atualizar o motorista.'); }
  };

  const remover = async (m) => {
    if (!confirm(`Remover o cadastro de "${m.nome}"? O histórico de entradas dele não é afetado.`)) return;
    try { await API.acesso.motoristas.remover(m.id); await refresh(); }
    catch (e) { alert(e.message || 'Não foi possível remover.'); }
  };

  const filtrados = motoristas.filter(m => {
    if (!busca.trim()) return true;
    const q = busca.toLowerCase();
    return m.nome.toLowerCase().includes(q) || m.documento.includes(busca) || (m.empresa || '').toLowerCase().includes(q);
  });

  return (
    <div className="main-pad">
      <div className="page-title-row">
        <div className="page-title">
          <h2>🪪 Motoristas Frequentes</h2>
          <p>Cadastre quem entrega todo dia — o QR evita redigitar os dados na doca</p>
        </div>
        {canEdit && <button className="btn btn-primary" onClick={openNew}><Icon name="plus" size={14}/> Novo Motorista</button>}
      </div>

      {erroCarga && (
        <div className="card" style={{ padding: 16, marginBottom: 16, background: 'var(--err-bg)', border: '1px solid #f5c2c2' }}>
          <div style={{ color: 'var(--err)', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{erroCarga}</div>
          <button className="btn btn-ghost" onClick={refresh}>Tentar de novo</button>
        </div>
      )}

      <div className="card" style={{ marginBottom: 16 }}>
        <input placeholder="Buscar por nome, CPF/CNH ou empresa..." value={busca} onChange={e => setBusca(e.target.value)}/>
      </div>

      {carregando ? (
        <div className="card" style={{ padding: 20, color: 'var(--ink-3)', fontSize: 13 }}>Carregando…</div>
      ) : filtrados.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 40, color: 'var(--ink-3)' }}>
          <Icon name="user" size={32} style={{ margin: '0 auto 12px', display: 'block' }}/>
          <p>Nenhum motorista cadastrado.</p>
          {canEdit && <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={openNew}><Icon name="plus" size={14}/> Cadastrar primeiro motorista</button>}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtrados.map(m => {
            const tipo = TIPOS_VEICULO.find(t => t.id === m.tipoVeiculo);
            return (
              <div key={m.id} className="card" style={{ opacity: m.ativo ? 1 : 0.55 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                  <div style={{ width: 40, height: 40, borderRadius: 10, background: 'var(--surface-2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon name={tipo?.icon || 'user'} size={18}/>
                  </div>
                  <div style={{ flex: 1, minWidth: 180 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{m.nome} {!m.ativo && <span className="status-pill muted" style={{ marginLeft: 6 }}><span className="ind"/>Inativo</span>}</div>
                    <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>
                      {m.documento}{m.telefone ? ` · ${m.telefone}` : ''}{m.empresa ? ` · ${m.empresa}` : ''}{tipo ? ` · ${tipo.label}` : ''}{m.placaHabitual ? ` · ${m.placaHabitual}` : ''}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 2, fontFamily: "'JetBrains Mono',monospace" }}>{m.codigoQr}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => setQrDe(m)} title="QR Code"><Icon name="qr-code" size={13}/></button>
                    {canEdit && <button className="btn btn-ghost btn-sm" onClick={() => openEdit(m)}><Icon name="edit" size={13}/></button>}
                    {canEdit && <button className="btn btn-ghost btn-sm" onClick={() => alternarAtivo(m)}>{m.ativo ? 'Desativar' : 'Ativar'}</button>}
                    {canDelete && <button className="btn btn-danger btn-sm" onClick={() => remover(m)}><Icon name="trash" size={13}/></button>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {modal && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal-card" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <button className="close-x" onClick={() => setModal(null)}><Icon name="x" size={16}/></button>
            <h3>{modal === 'new' ? 'Novo Motorista Frequente' : 'Editar Motorista'}</h3>
            <p style={{ fontSize: 13, color: 'var(--ink-3)', marginBottom: 20 }}>O QR é gerado automaticamente ao salvar</p>
            <div className="seg-field-row">
              <div className="field"><label>Nome <span className="req">*</span></label>
                <input value={form.nome} onChange={e => setForm(f => ({ ...f, nome: e.target.value }))} placeholder="Nome completo"/>
              </div>
              <div className="field"><label>CPF/CNH <span className="req">*</span></label>
                <input value={form.documento} onChange={e => setForm(f => ({ ...f, documento: maskDocumento(e.target.value) }))} inputMode="numeric" placeholder="Só números"/>
                {documentoPareceCpfValido(form.documento) && <div className="hint" style={{ color:'var(--ok)' }}>✓ CPF válido</div>}
              </div>
            </div>
            <div className="seg-field-row">
              <div className="field"><label>Telefone</label>
                <input value={form.telefone} onChange={e => setForm(f => ({ ...f, telefone: maskPhone(e.target.value) }))} placeholder="(XX) XXXXX-XXXX" maxLength={15}/>
              </div>
              <div className="field"><label>Empresa / Transportadora</label>
                <input value={form.empresa} onChange={e => setForm(f => ({ ...f, empresa: e.target.value }))}/>
              </div>
            </div>
            <div className="seg-field-row">
              <div className="field"><label>Tipo de Veículo habitual</label>
                <select value={form.tipoVeiculo} onChange={e => setForm(f => ({ ...f, tipoVeiculo: e.target.value }))}>
                  <option value="">Não informado</option>
                  {TIPOS_VEICULO.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                </select>
              </div>
              <div className="field"><label>Placa habitual</label>
                <input value={form.placaHabitual} onChange={e => setForm(f => ({ ...f, placaHabitual: fmtPlaca(e.target.value) }))} placeholder="ABC1234"/>
                <div className="hint">Referência — na entrada, a placa de verdade é sempre conferida/digitada.</div>
              </div>
            </div>
            {err && <p style={{ color: 'var(--err)', fontSize: 12, marginBottom: 12 }}>{err}</p>}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => setModal(null)}>Cancelar</button>
              <button className="btn btn-primary" onClick={save} disabled={busy}>{busy ? 'Salvando...' : 'Salvar Motorista'}</button>
            </div>
          </div>
        </div>
      )}

      {qrDe && <AcessoMotoristaQRModal motorista={qrDe} onClose={() => setQrDe(null)}/>}
    </div>
  );
}
